import NeptuneElement from '../../NeptuneElement.js';
import { i18nMixin } from '../../../utils/i18nMixin.js';
import { i18n } from '../../../utils/i18n.js';

/**
 * @class InlineNotification
 * @extends NeptuneElement
 *
 * Inline notification component for displaying contextual messages with status indicators.
 * Renders a colored banner with an icon, message text, optional action button, and optional close button.
 * Supports four notification types (info, success, warning, error), each with its own icon and color scheme.
 * Messages persist until the user dismisses them via close button or can be hidden programmatically.
 */
class InlineNotification extends i18nMixin(NeptuneElement) {

    static ICON_MAP = {
        info: 'fa-solid fa-circle-info',
        success: 'fa-solid fa-circle-check',
        warning: 'fa-solid fa-circle-exclamation',
        error: 'fa-solid fa-circle-xmark'
    };

    templateMarkup = `
        <div role="status" aria-live="polite" aria-atomic="true" id="inline-notification">
            <i></i>
            <span class="font-size-md">
                <slot name="text"></slot>
            </span>
            <neptune-button type="tertiary" size="small" aria-label="Action"></neptune-button>
            <neptune-button type="tertiary" size="small" icon-end="fa-solid fa-xmark" aria-label="Dismiss notification"></neptune-button>
        </div>
    `;

    componentCssUrl = '/src/components/core/inlineNotification/inline-notification.css';

    #actionButtonElement = null;
    #closeButtonElement = null;
    #iconElement = null;
    #handleActionButtonClickBound = null;
    #handleCloseButtonClickBound = null;

    /**
     * Creates a new InlineNotification instance.
     * Delegates to the HTMLElement constructor via NeptuneElement.
     */
    constructor() {
        super();
    }

    /**
     * Invoked when the component is added to the DOM.
     * Sets up localization, applies translations, attaches event listeners, and processes initial attributes.
     * @returns {Promise<void>}
     */
    async connectedCallback() {
        await super.connectedCallback();
        const culture = this.getAttribute('culture');
        if (culture) {
            i18n.setLocale(culture);
        }
        this.#applyTranslations();
        this.setEventListeners();
        this.processInitialAttributesAfterRender(InlineNotification.observedAttributes);
    }

    /**
     * Invoked when the component is removed from the DOM.
     * Cleans up all event listeners.
     * @returns {Promise<void>}
     */
    async disconnectedCallback() {
        await super.disconnectedCallback();
        this.removeEventListeners();
    }

    static get observedAttributes() {
        return ['type', 'action-label', 'dismissible', 'culture'];
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (!this.isRendered) {
            return;
        }
        this.processAttribute(name, oldValue, newValue);
    }

    processAttribute(name, oldValue, newValue) {
        if (oldValue === newValue) {
            return;
        }
        switch (name) {
            case 'culture':
                if (newValue) {
                    i18n.setLocale(newValue);
                    this.onLocaleChange();
                }
                break;
            case 'type':
                if (newValue !== null) {
                    this.validatePropertyValue(name, newValue, Object.keys(InlineNotification.ICON_MAP));
                }
                this.#processTypeAttribute(newValue);
                break;
            case 'action-label':
                this.#processActionLabelAttribute(newValue);
                break;
            case 'dismissible':
                this.#processDismissibleAttribute(newValue);
                break;
            default:
                break;
        }
    }

    /**
     * Attaches event listeners for the component.
     * Initializes button click handlers and processes all initial attributes.
     * @returns {void}
     */
    setEventListeners() {
        this.#actionButtonElement = this.shadowRoot?.querySelector('div > neptune-button:first-of-type');
        this.#closeButtonElement = this.shadowRoot?.querySelector('div > neptune-button:last-of-type');
        this.#iconElement = this.shadowRoot?.querySelector('div > i');

        this.#handleActionButtonClickBound = this.#handleActionButtonClick.bind(this);
        this.#handleCloseButtonClickBound = this.#handleCloseButtonClick.bind(this);

        this.#actionButtonElement?.addEventListener('click', this.#handleActionButtonClickBound);
        this.#closeButtonElement?.addEventListener('click', this.#handleCloseButtonClickBound);
        this.#processTypeAttribute(this.getAttribute('type'));
        this.#processActionLabelAttribute(this.getAttribute('action-label'));
        this.#processDismissibleAttribute(this.getAttribute('dismissible'));
        this.#applyTranslations();
    }

    /**
     * Re-applies translations to button aria-labels when the active locale changes.
     * Called whenever the culture attribute is updated.
     * @returns {void}
     */
    onLocaleChange() {
        if (this.isRendered) {
            this.#applyTranslations();
        }
    }

    /**
     * Applies translated aria-labels to both action and close buttons.
     * Uses provided action-label attribute if set, otherwise falls back to translated defaults.
     * @returns {void}
     * @private
     */
    #applyTranslations() {
        if (!this.#actionButtonElement || !this.#closeButtonElement) {
            return;
        }

        const actionLabel = this.getAttribute('action-label');
        const hasActionLabel = Boolean(actionLabel?.trim());

        if (hasActionLabel) {
            this.#actionButtonElement.setAttribute('aria-label', actionLabel);
        } else {
            this.#actionButtonElement.setAttribute('aria-label', this.translate('INLINE_NOTIFICATION.ACTION'));
        }

        this.#closeButtonElement.setAttribute('aria-label', this.translate('INLINE_NOTIFICATION.DISMISS'));
    }

    /**
     * Removes all event listeners and nullifies internal element references.
     * Called automatically during disconnectedCallback.
     * @returns {void}
     */
    removeEventListeners() {
        this.#actionButtonElement?.removeEventListener('click', this.#handleActionButtonClickBound);
        this.#closeButtonElement?.removeEventListener('click', this.#handleCloseButtonClickBound);
        this.#actionButtonElement = null;
        this.#closeButtonElement = null;
        this.#iconElement = null;
        this.#handleActionButtonClickBound = null;
        this.#handleCloseButtonClickBound = null;
    }

    /**
     * Hides the notification by setting the hidden attribute.
     * Called automatically when the close button is clicked.
     * Can also be invoked programmatically by consumers.
     * @returns {void}
     */
    close() {
        this.setAttribute('hidden', '');
    }

    /**
     * Updates the icon element based on the notification type.
     * Applies the appropriate Font Awesome icon classes and shows/hides the icon accordingly.
     * Defaults to 'info' type when no type is provided.
     * @param {string|null} typeValue - The notification type (info, success, warning, error, or null/undefined for default)
     * @returns {void}
     * @private
     */
    #processTypeAttribute(typeValue) {
        if (!this.#iconElement) {return;}
        const type = typeValue || 'info';
        
        Object.values(InlineNotification.ICON_MAP).forEach(classElement => {
            this.#iconElement.classList.remove(...classElement.split(' '));
        });
        if (InlineNotification.ICON_MAP[type]) {
            this.#iconElement.classList.add(...InlineNotification.ICON_MAP[type].split(' '));
            this.#iconElement.style.display = '';
            this.#iconElement.setAttribute('aria-label', type);
        } else {
            this.#iconElement.style.display = 'none';
            this.#iconElement.removeAttribute('aria-label');
        }
    }

    /**
     * Updates the action button's text and visibility based on the action-label attribute.
     * Shows button when action-label is provided and set, hides otherwise.
     * @param {string|null} actionLabelValue - The action label text, or null if not set
     * @returns {void}
     * @private
     */
    #processActionLabelAttribute(actionLabelValue) {
        if (!this.#actionButtonElement) {return;}

        const hasActionLabel = Boolean(actionLabelValue?.trim());
        this.#actionButtonElement.textContent = actionLabelValue ?? '';
        this.#actionButtonElement.style.display = hasActionLabel ? '' : 'none';
        const ariaLabel = hasActionLabel ? actionLabelValue : this.translate('INLINE_NOTIFICATION.ACTION');
        this.#actionButtonElement.setAttribute('aria-label', ariaLabel);
    }

    /**
     * Updates the close button's visibility based on the dismissible attribute.
     * Shows close button when dismissible attribute is present, hides otherwise.
     * @param {string|null} dismissibleValue - The dismissible attribute value (presence alone enables)
     * @returns {void}
     * @private
     */
    #processDismissibleAttribute(dismissibleValue) {
        if (!this.#closeButtonElement) {return;}
        const isDismissible = dismissibleValue !== null && dismissibleValue !== undefined;
        this.#closeButtonElement.style.display = isDismissible ? '' : 'none';
    }

    /**
     * Handles close button click events.
     * Hides the notification and dispatches a close event for consumer handling.
     * @returns {void}
     * @private
     */
    #handleCloseButtonClick() {
        this.close();
        this.dispatchEvent(new CustomEvent('neptune-inline-notification-close', {
            bubbles: true,
            composed: true
        }));
    }

    /**
     * Handles action button click events.
     * Dispatches an action event with the current action-label for consumer custom logic.
     * @returns {void}
     * @private
     */
    #handleActionButtonClick() {
        this.dispatchEvent(new CustomEvent('neptune-inline-notification-action', {
            bubbles: true,
            composed: true,
            detail: {
                actionLabel: this.getAttribute('action-label')
            }
        }));
    }
}

customElements.define('neptune-inline-notification', InlineNotification);
export default InlineNotification;
